import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import KkpBilanAudiencePreview from '../../components/kkp/KkpBilanAudiencePreview';
import { AppFilterBar, AppFilterSelect, AppFilterCommercialSelect, AppFilterField, AppFilterMonthRange } from '../../components/filters';
import KkpPlanningPageActions from '../../components/kkp/KkpPlanningPageActions';
import KkpPlanningEmptyState from '../../components/kkp/KkpPlanningEmptyState';
import kkpService from '../../services/kkpService';
import { notify } from '../../components/common/AppToast';
import { getDefaultBilanPeriod, MOIS_OPTIONS } from '../../utils/kkpBilanAudienceUtils';
import { matchCommercialOption } from '../../utils/matchCommercialOption';
import { AppAlert } from '../../components/ui';
import '../../styles/kkp-planning-page.scss';

/**
 * Cherche le contact client Dolibarr rattaché à une raison sociale,
 * et le commercial associé via le représentant Dolibarr.
 * @returns {Promise<{
 *   client_label: string,
 *   commercial_id: number|null,
 *   commercial_nom: string,
 *   commercial_email: string,
 *   commercial_emails: string[],
 * }>}
 */
async function resolveDolibarrClientPrefill(entrepriseLabel, signal) {
  const empty = {
    client_label: '',
    commercial_id: null,
    commercial_nom: '',
    commercial_email: '',
    commercial_emails: [],
  };
  const query = String(entrepriseLabel ?? '').trim();
  if (query.length < 2) {
    return empty;
  }

  const matches = await searchDolibarrCompaniesForLabel(query, signal);
  const candidate = findBestDolibarrCompanyMatch(matches, query);
  const thirdpartyId = Number(candidate?.id) || 0;
  if (thirdpartyId <= 0) {
    return empty;
  }

  const detail = await kkpService.getDolibarrClient(thirdpartyId);
  return {
    // Contact personne rattaché Dolibarr ; sinon raison sociale officielle Dolibarr.
    client_label: String(detail?.client_label ?? detail?.nom ?? '').trim(),
    commercial_id: Number(detail?.commercial_id) > 0 ? Number(detail.commercial_id) : null,
    commercial_nom: String(detail?.commercial_nom ?? '').trim(),
    commercial_email: String(detail?.commercial_email ?? '').trim(),
    commercial_emails: Array.isArray(detail?.commercial_emails) ? detail.commercial_emails : [],
  };
}

/**
 * Cherche le tiers Dolibarr même si le libellé planning diffère légèrement
 * (ex. "Chateau de Sallay" vs "Château Le Sallay").
 */
async function searchDolibarrCompaniesForLabel(query, signal) {
  const seen = new Map();
  const queries = buildDolibarrSearchQueries(query);

  for (const q of queries) {
    const matches = await kkpService.searchDolibarrClients(q, {
      limit: 15,
      dolibarrOnly: true,
      signal,
    });
    (matches ?? []).forEach((item) => {
      const id = Number(item?.id) || 0;
      if (id <= 0 || item?.entreprise || seen.has(id)) {
        return;
      }
      seen.set(id, item);
    });
  }

  return Array.from(seen.values());
}

function buildDolibarrSearchQueries(query) {
  const raw = String(query ?? '').trim();
  if (!raw) return [];

  const core = normalizeCompanyCore(raw);
  const tokens = core.split(' ').filter((token) => token.length >= 3);
  const queries = [raw];

  if (core && core !== normalizeCompanyLabel(raw)) {
    queries.push(core);
  }

  // Mot le plus discriminant (ex. "Sallay") pour rattraper de/le/la.
  const distinctive = [...tokens].sort((a, b) => b.length - a.length)[0];
  if (distinctive) {
    queries.push(distinctive);
  }

  // Deux derniers tokens significatifs (ex. "Chateau Sallay").
  if (tokens.length >= 2) {
    queries.push(tokens.slice(-2).join(' '));
  }

  return [...new Set(queries.filter(Boolean))];
}

function findBestDolibarrCompanyMatch(matches, query) {
  const list = Array.isArray(matches) ? matches : [];
  if (!list.length) return null;

  const queryNorm = normalizeCompanyLabel(query);
  const queryCore = normalizeCompanyCore(query);

  let best = null;
  let bestScore = -1;

  list.forEach((item) => {
    if (Number(item?.id) <= 0 || item?.entreprise) {
      return;
    }

    const candidateLabel = String(item?.nom ?? '').trim();
    if (!candidateLabel) {
      return;
    }

    const candidateNorm = normalizeCompanyLabel(candidateLabel);
    const candidateCore = normalizeCompanyCore(candidateLabel);
    const candidateTokens = new Set(candidateCore.split(' ').filter(Boolean));
    const queryTokens = queryCore.split(' ').filter(Boolean);

    let score = 0;
    if (candidateNorm === queryNorm) score += 1000;
    if (candidateCore === queryCore) score += 900;
    if (candidateNorm.includes(queryNorm) || queryNorm.includes(candidateNorm)) score += 250;
    if (candidateCore.includes(queryCore) || queryCore.includes(candidateCore)) score += 220;

    queryTokens.forEach((token) => {
      if (candidateTokens.has(token)) score += 80;
    });

    if (queryTokens.length > 0 && queryTokens.every((token) => candidateTokens.has(token))) {
      score += 180;
    }

    if (score > bestScore) {
      best = item;
      bestScore = score;
    }
  });

  return bestScore >= 160
    ? best
    : null;
}

function normalizeCompanyLabel(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeCompanyCore(value) {
  return normalizeCompanyLabel(value)
    .split(' ')
    .filter((token) => token && !['de', 'du', 'des', 'la', 'le', 'les', 'd'].includes(token))
    .join(' ');
}

const KkpBilanAudiencePage = () => {
  const currentYear = new Date().getFullYear();
  const averageScopeOptions = useMemo(() => ([
    { value: 'all_clients', label: 'Moyenne tous clients' },
    { value: 'other_clients', label: 'Moyenne des autres clients' },
    { value: 'none', label: 'Pas de comparaison avec la moyenne' },
  ]), []);
  const [meta, setMeta] = useState(null);
  const [periode, setPeriode] = useState(() => getDefaultBilanPeriod());
  const [entreprise, setEntreprise] = useState([]);
  const [averageScope, setAverageScope] = useState(['all_clients']);
  const [contact, setContact] = useState('');
  const [resolvingClient, setResolvingClient] = useState(false);
  const [commercialNom, setCommercialNom] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const clientLookupAbortRef = useRef(null);

  const entrepriseValue = entreprise[0] || '';
  const commercialNomValue = commercialNom[0] || '';
  const averageScopeValue = averageScope[0] || 'all_clients';

  const loadMeta = useCallback(async (nextPeriode) => {
    setLoadingMeta(true);
    setError(null);
    try {
      const data = await kkpService.getBilanAudienceMeta(nextPeriode);
      setMeta(data);
      if (data.periode) {
        setPeriode({
          periode_debut_annee: data.periode.debut_annee,
          periode_debut_mois: data.periode.debut_mois,
          periode_fin_annee: data.periode.fin_annee,
          periode_fin_mois: data.periode.fin_mois,
        });
      }
      const entreprisesList = data.entreprises ?? [];
      setEntreprise((prev) => {
        const current = prev[0] || '';
        if (current && entreprisesList.includes(current)) return prev;
        return [];
      });
      // Ne pas forcer un commercial par défaut : il sera prérempli
      // à la sélection d'entreprise via la correspondance Dolibarr.
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingMeta(false);
    }
  }, []);

  useEffect(() => {
    if (!entrepriseValue) {
      setContact('');
    }
  }, [entrepriseValue]);

  useEffect(() => {
    loadMeta(getDefaultBilanPeriod());
  }, [loadMeta]);

  const handlePeriodeChange = async (nextPeriode) => {
    if (!nextPeriode) return;
    setPeriode(nextPeriode);
    setPreview(null);
    setResult(null);
    await loadMeta(nextPeriode);
  };

  const loadPreview = useCallback(async () => {
    if (!entrepriseValue) {
      setPreview(null);
      return;
    }
    setLoadingPreview(true);
    setError(null);
    try {
      const data = await kkpService.previewBilanAudience({
        ...periode,
        entreprise: entrepriseValue,
        average_scope: averageScopeValue,
      });
      setPreview(data.channels ?? {});
    } catch (err) {
      setError(err.message);
      setPreview(null);
    } finally {
      setLoadingPreview(false);
    }
  }, [periode, entrepriseValue, averageScopeValue]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const fillClientFromDolibarr = useCallback(async (entrepriseLabel) => {
    if (clientLookupAbortRef.current) {
      clientLookupAbortRef.current.abort();
    }
    const label = String(entrepriseLabel ?? '').trim();
    if (!label) {
      setContact('');
      setResolvingClient(false);
      return;
    }

    const controller = new AbortController();
    clientLookupAbortRef.current = controller;
    setResolvingClient(true);
    setContact('');
    try {
      const prefill = await resolveDolibarrClientPrefill(label, controller.signal);
      if (controller.signal.aborted) return;
      setContact(prefill.client_label);
      const matchedCommercial = matchCommercialOption(
        (meta?.commerciaux ?? []).map((c) => ({
          value: c.nom,
          label: c.nom,
          email: c.email,
        })),
        {
          commercial_id: prefill.commercial_id,
          commercial_nom: prefill.commercial_nom,
          commercial_email: prefill.commercial_email,
          commercial_emails: prefill.commercial_emails,
        },
      );
      if (matchedCommercial) {
        setCommercialNom([matchedCommercial.value]);
      } else {
        setCommercialNom([]);
      }
    } catch (err) {
      if (err?.name === 'AbortError' || controller.signal.aborted) return;
      if (!controller.signal.aborted) {
        setContact('');
      }
    } finally {
      if (!controller.signal.aborted) {
        setResolvingClient(false);
      }
    }
  }, [meta?.commerciaux]);

  useEffect(() => () => {
    clientLookupAbortRef.current?.abort();
  }, []);

  const contactValue = String(contact ?? '').trim();

  const handleGenerate = useCallback(async () => {
    if (!meta?.can_generate || !entrepriseValue || !contactValue || !commercialNomValue || generating) {
      return;
    }

    setGenerating(true);
    setError(null);
    setResult(null);
    try {
      const data = await notify.run(
        () => kkpService.generateBilanAudience({
          ...periode,
          entreprise: entrepriseValue,
          contact: contactValue,
          commercial_nom: commercialNomValue,
          average_scope: averageScopeValue,
        }),
        {
          loading: 'Génération de la présentation en cours…',
          success: 'Présentation générée',
        },
      );
      setResult(data);
      window.open(data.presentation_url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  }, [meta?.can_generate, entrepriseValue, contactValue, commercialNomValue, generating, periode, averageScopeValue]);

  const handleResetFilters = useCallback(async () => {
    clientLookupAbortRef.current?.abort();
    const defaults = getDefaultBilanPeriod();
    setEntreprise([]);
    setAverageScope(['all_clients']);
    setContact('');
    setResolvingClient(false);
    setCommercialNom([]);
    setPreview(null);
    setResult(null);
    setPeriode(defaults);
    await loadMeta(defaults);
  }, [loadMeta]);

  const annees = useMemo(() => {
    const fromMeta = meta?.annees?.length ? meta.annees : [];
    const merged = new Set([
      ...fromMeta,
      periode.periode_debut_annee,
      periode.periode_fin_annee,
      currentYear,
      currentYear + 1,
      currentYear - 1,
    ]);
    return [...merged].filter((year) => year > 2000).sort((a, b) => b - a);
  }, [meta?.annees, periode, currentYear]);

  const periodeLabel = (() => {
    const moisNoms = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    const debut = `${moisNoms[(periode.periode_debut_mois || 1) - 1]} ${periode.periode_debut_annee}`;
    const fin = `${moisNoms[(periode.periode_fin_mois || 1) - 1]} ${periode.periode_fin_annee}`;
    return `${debut} — ${fin}`;
  })();

  const entreprises = meta?.entreprises ?? [];
  const commercialOptions = useMemo(
    () => (meta?.commerciaux ?? []).map((c) => ({ value: c.nom, label: c.nom })),
    [meta?.commerciaux],
  );
  const anneeOptions = useMemo(
    () => annees.map((year) => ({ value: String(year), label: String(year) })),
    [annees],
  );
  const moisOptions = useMemo(
    () => MOIS_OPTIONS.map((m) => ({ value: String(m.value), label: m.label })),
    [],
  );
  const entrepriseOptions = useMemo(
    () => entreprises.map((e) => ({ value: e, label: e })),
    [entreprises],
  );

  const canGenerate = Boolean(
    meta?.can_generate
    && entrepriseValue
    && contactValue
    && commercialNomValue
    && !generating,
  );

  const pageActions = useMemo(() => {
    const items = [
      {
        key: 'generate',
        label: generating ? 'Génération…' : 'Générer la présentation',
        icon: 'mdi:presentation',
        onClick: handleGenerate,
        disabled: !canGenerate,
        loading: generating,
        variant: 'primary',
      },
    ];
    if (result?.presentation_url) {
      items.push({
        key: 'open-slides',
        label: 'Ouvrir Google Slides',
        icon: 'mdi:open-in-new',
        onClick: () => window.open(result.presentation_url, '_blank', 'noopener,noreferrer'),
      });
    }
    return items;
  }, [result?.presentation_url, generating, canGenerate, handleGenerate]);

  return (
    <MasterLayout>
      <Breadcrumb
        title="Bilan audience"
        subtitle={periodeLabel}
        actions={<KkpPlanningPageActions actions={pageActions} />}
      />

      {error && (
        <AppAlert className="py-2">{error}</AppAlert>
      )}

      {!loadingMeta && meta && !meta.google_configured && (
        <AppAlert variant="warning" className="py-2">
          La génération Google Slides n&apos;est pas configurée côté serveur
          (variable <code>GOOGLE_SERVICE_ACCOUNT_JSON</code>).
          Vous pouvez prévisualiser les canaux disponibles, mais pas créer la présentation.
        </AppAlert>
      )}

      {!loadingMeta && meta?.google_configured && !meta?.saves_to_user_drive && !meta?.drive_folder_configured && (
        <AppAlert variant="warning" className="py-2">
          Impossible d&apos;enregistrer dans un Drive utilisateur : connectez-vous avec un compte
          identifié, ou configurez un dossier partagé (<code>KKP_BILAN_DRIVE_FOLDER_ID</code>).
        </AppAlert>
      )}

      {loadingMeta && !meta ? (
        <KkpPlanningEmptyState loading />
      ) : (
        <>
          <AppFilterBar onReset={handleResetFilters} resetDisabled={loadingMeta}>
            <AppFilterMonthRange
              id="bilan-periode"
              label="Période"
              value={periode}
              onChange={handlePeriodeChange}
              monthOptions={moisOptions}
              yearOptions={anneeOptions}
              disabled={loadingMeta}
              col="col-12 col-lg-5"
            />
            <AppFilterSelect
              id="bilan-entreprise"
              label="Entreprise"
              col="col-12 col-md-6 col-lg-3"
              value={entreprise}
              onChange={(next) => {
                const selected = next.slice(-1);
                setEntreprise(selected);
                setResult(null);
                fillClientFromDolibarr(selected[0] || '');
              }}
              options={entrepriseOptions}
              multiple={false}
              placeholder="— Sélectionner —"
              emptyLabel="Aucune entreprise trouvée"
              disabled={loadingMeta || entreprises.length === 0}
            />
            <AppFilterSelect
              id="bilan-average-scope"
              label="Référence moyenne"
              col="col-12 col-md-6 col-lg-3"
              value={averageScope}
              onChange={(next) => {
                setAverageScope(next.slice(-1));
                setResult(null);
              }}
              options={averageScopeOptions}
              multiple={false}
              placeholder="— Sélectionner —"
              disabled={loadingMeta}
            />
            <AppFilterField col="col-12 col-md-6 col-lg-3" label="Nom du client">
              <input
                type="text"
                className="form-control form-control-sm"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                placeholder={
                  resolvingClient
                    ? 'Recherche contact Dolibarr…'
                    : 'Contact rattaché ou saisie libre…'
                }
                disabled={resolvingClient}
                required
                autoComplete="off"
              />
            </AppFilterField>
            <AppFilterCommercialSelect
              id="bilan-commercial"
              col="col-12 col-md-6 col-lg-3"
              value={commercialNom}
              onChange={setCommercialNom}
              options={commercialOptions}
              disabled={loadingMeta || commercialOptions.length === 0}
            />
          </AppFilterBar>

          <div className="card kkp-planning-results-card kkp-bilan-results-card">
            <div className="card-body">
              <KkpBilanAudiencePreview
                channels={preview ?? {}}
                loading={loadingPreview}
                entreprise={entrepriseValue}
                periodeLabel={periodeLabel}
                averageScope={averageScopeValue}
              />
            </div>
          </div>
        </>
      )}
    </MasterLayout>
  );
};

export default KkpBilanAudiencePage;
